// Carregando, vazio e falha são três estados diferentes, e é por isso que são
// três componentes. No painel antigo qualquer um dos três virava tela em branco
// ou um traço, e o dev não sabia se o acervo estava vazio ou se a API tinha
// caído; o `withState` de app.js:35-44 foi o conserto, e aqui ele volta como
// componente.
//
// A regra que vem de lá e continua valendo: vazio explica o motivo e oferece a
// saída; falha sempre oferece o caminho de volta.
import type { ReactNode } from 'react';

export function Carregando({ rotulo = 'carregando…' }: { rotulo?: string }) {
  return (
    <div className="loading">
      <span className="sp" />
      <span>{rotulo}</span>
    </div>
  );
}

export interface VazioProps {
  titulo: string;
  children: ReactNode;
  acao?: { rotulo: string; ao: () => void };
  comando?: string;
}

export function Vazio({ titulo, children, acao, comando }: VazioProps) {
  return (
    <div className="blank">
      <h4>{titulo}</h4>
      <p>{children}</p>
      {(acao || comando) && (
        <div className="how">
          {acao && <button type="button" className="btn sm" onClick={acao.ao}>{acao.rotulo}</button>}
          {comando && <code>{comando}</code>}
        </div>
      )}
    </div>
  );
}

export function Falha({ erro, rotulo = 'esta seção', aoRepetir }: {
  erro: unknown;
  rotulo?: string;
  aoRepetir?: () => void;
}) {
  const msg = erro instanceof Error ? erro.message : String(erro || 'erro desconhecido');
  return (
    <div className="banner-bad">
      <div>
        <b>Não deu para carregar {rotulo}.</b>
        <div className="muted" style={{ marginTop: 4 }}>{msg}</div>
      </div>
      {aoRepetir && <button type="button" className="btn" onClick={aoRepetir}>Tentar de novo</button>}
    </div>
  );
}

/**
 * Os três estados de uma consulta em um lugar só, para nenhuma tela decidir
 * sozinha o que fazer quando `data` ainda é undefined.
 *
 * `vazio` é separado de `children` de propósito: "não tem dado" e "não deu para
 * buscar" são respostas diferentes para o usuário, e a tela que não distingue
 * as duas é justamente a que manda o dev procurar defeito onde não tem.
 */
export interface ComEstadoProps<T> {
  consulta: { data?: T; isPending: boolean; error: unknown; refetch: () => void };
  rotulo: string;
  children: (dados: T) => ReactNode;
  vazio?: (dados: T) => ReactNode;
}

export function ComEstado<T>({ consulta, rotulo, children, vazio }: ComEstadoProps<T>) {
  if (consulta.isPending) return <Carregando rotulo={rotulo} />;
  if (consulta.error) return <Falha erro={consulta.error} rotulo={rotulo} aoRepetir={consulta.refetch} />;
  const dados = consulta.data as T;
  const semDado = vazio?.(dados);
  return <>{semDado ?? children(dados)}</>;
}

/**
 * Badge de aba: carregando, número real (inclusive zero) ou falha declarada.
 * Zero é resposta e aparece como zero; `?` sem estado seria mentira por omissão.
 */
export function Contador({ estado, valor, titulo, alerta }: {
  estado: 'carregando' | 'erro' | 'ok';
  valor?: string | number;
  titulo?: string;
  /** amarelo quando há coisa esperando gente, como no `warn` de knowledge.js:44 */
  alerta?: boolean;
}) {
  if (estado === 'carregando') return <span className="badge b-load" title="carregando…" />;
  if (estado === 'erro') {
    return <span className="badge b-err" title={titulo || 'não deu para carregar — recarregue a página'}>!</span>;
  }
  return <span className={`badge${alerta ? ' warn' : ''}`} title={titulo || ''}>{valor ?? 0}</span>;
}
